import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { 
  FileText, Download, Share2, Users, Pill, Heart, Phone,
  RefreshCw, CheckCircle2, Calendar, AlertTriangle, Mail
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const CarePlanExport = () => {
  const [carePlan, setCarePlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareForm, setShowShareForm] = useState(false);
  const [shareDetails, setShareDetails] = useState({
    email: '',
    recipient_name: '',
    relationship: '',
    message: ''
  });
  const { toast } = useToast();
  
  const token = localStorage.getItem('token');

  const fetchCarePlan = useCallback(async () => {
    try {
      // Using sample patient ID - in production, would get from context
      const patientId = 'sample_patient_1';
      const res = await fetch(`${API}/api/phase46/care-plan/export/${patientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCarePlan(data.care_plan);
      }
    } catch (err) {
      console.error('Error fetching care plan:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCarePlan();
  }, [fetchCarePlan]);

  const shareCarePlan = async () => {
    try {
      const res = await fetch(`${API}/api/phase46/care-plan/share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patient_id: 'sample_patient_1',
          ...shareDetails
        })
      });
      
      if (res.ok) {
        toast({ 
          title: "Care Plan Shared", 
          description: `Care plan sent to ${shareDetails.email}` 
        });
        setShowShareForm(false);
        setShareDetails({ email: '', recipient_name: '', relationship: '', message: '' });
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to share care plan", variant: "destructive" });
    }
  };

  const downloadPDF = () => {
    // In production, would generate actual PDF
    toast({ 
      title: "Downloading...", 
      description: "Care plan PDF is being prepared" 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="care-plan-export">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FileText className="h-7 w-7 text-teal-600" />
            Care Plan Overview
          </h1>
          <p className="text-gray-600">Review and share care plan with family members</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={downloadPDF}
            data-testid="download-pdf-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
          <Button 
            onClick={() => setShowShareForm(true)}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="share-plan-btn"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share with Family
          </Button>
        </div>
      </div>

      {/* Patient Info */}
      {carePlan && (
        <>
          <Card className="border-l-4 border-l-teal-500">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-semibold">{carePlan.patient_name}</h2>
                  <p className="text-sm text-gray-500">
                    Care Plan Generated: {new Date(carePlan.generated_at).toLocaleDateString()}
                  </p>
                </div>
                <Badge className="bg-teal-100 text-teal-800">Active</Badge>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Diagnoses */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Heart className="h-5 w-5 text-red-500" />
                  Diagnoses
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {carePlan.diagnoses?.map((dx, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium">{dx.name}</p>
                        <p className="text-xs text-gray-500">{dx.code}</p>
                      </div>
                      {dx.primary && (
                        <Badge variant="outline" className="text-red-600 border-red-200">
                          Primary
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Care Team */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-500" />
                  Care Team
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {carePlan.care_team?.map((member, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium">{member.name}</p>
                        <p className="text-xs text-gray-500">{member.role}</p>
                      </div>
                      <a 
                        href={`tel:${member.phone}`}
                        className="text-teal-600 hover:underline text-sm"
                      >
                        {member.phone}
                      </a>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Goals of Care */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  Goals of Care
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {carePlan.goals_of_care?.map((goal, i) => (
                    <div key={i} className="p-2 bg-gray-50 rounded">
                      <div className="flex justify-between items-center">
                        <p className="font-medium">{goal.goal}</p>
                        <Badge 
                          className={
                            goal.status === 'Achieved' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-blue-100 text-blue-800'
                          }
                        >
                          {goal.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Target: {goal.target}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Medications */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Pill className="h-5 w-5 text-purple-500" />
                  Comfort Medications
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {carePlan.medications?.map((med, i) => (
                    <div key={i} className="p-2 bg-gray-50 rounded">
                      <p className="font-medium">{med.name}</p>
                      <p className="text-sm text-gray-600">{med.dose} - {med.frequency}</p>
                      <p className="text-xs text-gray-500">For: {med.purpose}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Visit Schedule */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-orange-500" />
                  Visit Schedule
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {carePlan.visit_schedule?.map((visit, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium">{visit.discipline}</p>
                        <p className="text-xs text-gray-500">{visit.frequency}</p>
                      </div>
                      <span className="text-sm text-teal-600">{visit.next_visit}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Emergency Contacts */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Phone className="h-5 w-5 text-red-500" />
                  Emergency Contacts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {carePlan.emergency_contacts?.map((contact, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium">{contact.name}</p>
                        <p className="text-xs text-gray-500">{contact.role}</p>
                      </div>
                      <a 
                        href={`tel:${contact.phone}`}
                        className="text-red-600 hover:underline font-medium"
                      >
                        {contact.phone}
                      </a>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Advance Directives */}
          <Card className="border-amber-200 bg-amber-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Advance Directives
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-sm text-gray-500">DNR Status</p>
                  <Badge className={carePlan.advance_directives?.dnr ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                    {carePlan.advance_directives?.dnr ? 'DNR' : 'Full Code'}
                  </Badge>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-sm text-gray-500">Healthcare Proxy</p>
                  <p className="font-medium">{carePlan.advance_directives?.healthcare_proxy || 'Not assigned'}</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-sm text-gray-500">Comfort Measures</p>
                  <Badge className={carePlan.advance_directives?.comfort_measures_only ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-800'}>
                    {carePlan.advance_directives?.comfort_measures_only ? 'Yes' : 'No'}
                  </Badge>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-sm text-gray-500">Hospitalization</p>
                  <Badge className={carePlan.advance_directives?.no_hospitalization ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800'}>
                    {carePlan.advance_directives?.no_hospitalization ? 'Declined' : 'Allowed'}
                  </Badge>
                </div>
              </div>
              {carePlan.advance_directives?.notes && (
                <p className="mt-4 p-3 bg-white rounded-lg text-sm text-gray-700">
                  <strong>Notes:</strong> {carePlan.advance_directives.notes}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Comfort Measures */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Comfort Measures</CardTitle>
              <CardDescription>Non-pharmacological comfort interventions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {carePlan.comfort_measures?.map((measure, i) => (
                  <Badge key={i} variant="outline" className="text-teal-600 border-teal-200 px-3 py-1">
                    {measure}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Share Form Modal */}
      {showShareForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-teal-600" />
                Share Care Plan
              </CardTitle>
              <CardDescription>
                Send a copy of the care plan to a family member
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Recipient Email</label>
                <Input
                  type="email"
                  value={shareDetails.email}
                  onChange={(e) => setShareDetails({ ...shareDetails, email: e.target.value })}
                  placeholder="family@example.com"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Recipient Name</label>
                <Input
                  value={shareDetails.recipient_name}
                  onChange={(e) => setShareDetails({ ...shareDetails, recipient_name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Relationship</label>
                <Input
                  value={shareDetails.relationship}
                  onChange={(e) => setShareDetails({ ...shareDetails, relationship: e.target.value })}
                  placeholder="Son, Daughter, etc."
                />
              </div>
              <div>
                <label className="text-sm font-medium">Personal Message (Optional)</label>
                <Textarea
                  value={shareDetails.message}
                  onChange={(e) => setShareDetails({ ...shareDetails, message: e.target.value })}
                  placeholder="Add a personal message..."
                  rows={3}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button 
                  onClick={shareCarePlan} 
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                  disabled={!shareDetails.email}
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Send Care Plan
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowShareForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CarePlanExport;
